import { Controller } from '@hotwired/stimulus'
import Reveal from 'reveal.js'
import FsFx from 'reveal.js-fsfx'
import { hideAll } from 'tippy.js'

export default class extends Controller {
  static targets = ["innerSlide", "slideNumber"]

  // Content the teacher clicks deliberately must not flip the slide: native
  // interactive elements (slide bodies are authored rich text, so they can't
  // be tagged), Reveal/FsFx chrome, and app containers marked data-interactive
  // (question areas, lock overlays).
  static nonAdvancing = 'a, button, input, textarea, select, label, video, audio, iframe, [contenteditable], [data-interactive], .controls, .progress, .fsbutton'

  static values = {
    autorefresh: {
      type: Boolean,
      default: false
    },
    clickAdvance: {
      type: Boolean,
      default: false
    },
    hash: {
      type: Boolean,
      default: true
    },
    enableFullscreen: {
      type: Boolean,
      default: true
    },
    progress: {
      type: Boolean,
      default: true
    },
    readinessFrame: {
      type: Boolean,
      default: false
    },
    getReadyLabel: {
      type: String,
      default: ""
    },
    fullscreenLabel: {
      type: String,
      default: "Toggle fullscreen"
    }
  }

  connect() {
    this.initReveal()

    if (this.clickAdvanceValue) {
      this.clickToAdvance = (event) => this.advanceOnClick(event)
      // Capture phase: Reveal's own handlers exit the overview and jump to the
      // clicked slide before a bubble listener would run, so by then
      // isOverview() is false and we'd navigate a second time.
      this.element.addEventListener('click', this.clickToAdvance, true)
    }
  }

  disconnect() {
    if (this.clickToAdvance) this.element.removeEventListener('click', this.clickToAdvance, true)
  }

  // The deck's height in slide pixels: question slides fill it rather than the 16:9 canvas,
  // so on screens of another shape they use the space above and below (blocks.css).
  measureDeckHeight() {
    if (!this.deck) return

    this.element.style.setProperty('--deck-height', `${this.element.clientHeight / this.deck.getScale()}px`)
  }

  advanceOnClick(event) {
    if (this.deck.isOverview()) return
    if (event.target.closest(this.constructor.nonAdvancing)) return
    if (window.getSelection().toString() !== '') return

    // Kindle-style zones. Asymmetric split so a dead-centre click is
    // unambiguously "next", never a pixel away from going backward.
    const { left, width } = this.element.getBoundingClientRect()
    const clickedBackZone = (event.clientX - left) / width < 0.25

    clickedBackZone ? this.deck.prev() : this.deck.next()
  }

  initReveal() {
    this.deckReady = false
    const plugins = this.enableFullscreenValue ? [FsFx] : []

    const config = {
      plugins,
      // When our own page-count tag is present, disable Reveal's built-in slide
      // number (which renders as a hyperlink) and render the count ourselves.
      slideNumber: this.hasSlideNumberTarget ? false : 'c/t',
      hash: this.hashValue,
      progress: this.progressValue,
      // A modal over the slides owns the keyboard: Esc closes it, not Reveal's overview.
      keyboardCondition: () => !document.querySelector('#modal [role=dialog]')
    }

    if (this.enableFullscreenValue) {
      config.fsfx = {
        auto: {
          position: {
            left: '20px',
            top: '20px',
            right: false
          }
        }
      }
    }

    this.deck = new Reveal(config)

    this.deck.initialize({
      width: 1280,
      height: 720,
      // maxScale: 10.0
    })

    if (this.autorefreshValue) {
      const observer = new MutationObserver(() => {
        this.initReveal()
      })
      observer.observe(document.querySelector('.slides'), { childList: true, subtree: true })
    }

    // A new scale arrives with Reveal's resize event; a new height alone with the window's.
    this.deck.on('resize', () => this.measureDeckHeight())

    this.deck.on('ready', (event) => {
      this.measureDeckHeight()
      setTimeout(() => {
        this.handleSlideScrolling(event.currentSlide)
      }, 100)
      this.resizeVideo()
      this.resizeTableQuestion()
      this.resizeSpark()
      this.pinSlideNumberWidth()
      this.updateSlideNumber()
      this.labelFullscreenButtons()

      this.deckReady = true
      this.dispatch('ready', { detail: {
        index: this.deck.getIndices().h,
        totalSlides: this.deck.getTotalSlides()
      } })
    })

    this.deck.on('slidechanged', (event) => {
      if (event.previousSlide) {
        this.resetVideos(event.previousSlide)
      }

      if (event.previousSlide) {
        this.resetSlideScrolling(event.previousSlide)
      }
      setTimeout(() => {
        this.handleSlideScrolling(event.currentSlide)
      }, 100)
      this.resizeVideo()
      this.resizeTableQuestion()
      this.resizeSpark()
      this.updateSlideNumber()

      // Restoring a #/slide URL fires slidechanged during init; only user
      // navigation, which always happens after ready, should be published.
      if (this.deckReady) {
        this.dispatch('slidechanged', { detail: {
          index: event.indexh,
          previousIndex: event.previousSlide ? this.deck.getIndices(event.previousSlide).h : null,
          totalSlides: this.deck.getTotalSlides()
        } })
      }
    })
  }

  // Reserve the width of the widest value once, so the tag doesn't resize or clip
  // as you navigate. Only the current number varies (total is fixed), and digit
  // glyphs aren't equal width, so measure each repeated digit and keep the widest.
  pinSlideNumberWidth() {
    if (!this.hasSlideNumberTarget) return
    if (this.readinessFrameValue) return

    const total = this.deck.getTotalSlides()
    const digits = String(total).length

    let widest = 0
    for (let d = 0; d <= 9; d++) {
      this.#slideNumberLabel.textContent = `${String(d).repeat(digits)}/${total}`
      widest = Math.max(widest, this.slideNumberTarget.offsetWidth)
    }
    this.slideNumberTarget.style.width = `${widest}px`
  }

  updateSlideNumber() {
    if (!this.hasSlideNumberTarget) return

    const current = this.deck.getSlidePastCount() + 1
    const total = this.deck.getTotalSlides()

    if (this.readinessFrameValue) {
      // Frame 1 shows "Get ready"; lesson slides after it are numbered from 1.
      this.#slideNumberLabel.textContent =
        current === 1 ? this.getReadyLabelValue : `${current - 1}/${total - 1}`
    } else {
      this.#slideNumberLabel.textContent = `${current}/${total}`
    }
  }

  labelFullscreenButtons() {
    this.element.querySelectorAll('.fsbutton').forEach((button) => {
      button.setAttribute('aria-label', this.fullscreenLabelValue)
    })
  }

  get #slideNumberLabel() {
    return this.slideNumberTarget.querySelector('span') || this.slideNumberTarget
  }

  async resizeSpark() {
    const currentSlide = document.querySelector('section.present')

    if (!currentSlide.querySelector('[data-spark-slide]')) {
      return
    }
  }

  async resizeVideo() {
    const currentSlide = document.querySelector('section.present')
    if (!currentSlide.querySelector('[data-video]')) {
      return
    }

    const embed = currentSlide.querySelector('iframe')

    if (embed.src.includes('youtube.com')) {
      const videoId = embed.src.split('/embed/')[1].split('?')[0]

      const { height, width } = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}`)
        .then(res => res.json())

      const ratio = width / height

      const currentWidth = embed.offsetWidth

      currentSlide.style.top = 0
      embed.style.height = `${currentWidth / ratio}px`
      document.querySelector('.slides').style.height = `${currentWidth / ratio}px`
    }

  }

  resizeTableQuestion() {
    const currentSlide = document.querySelector('section.present')
    if (!currentSlide.querySelector('[data-type="sortable"]')) {
      return
    }

    const tableQuestion = currentSlide.querySelector('[data-type="sortable"]')

    // find the largest span within a data-label-cell item
    const spans = tableQuestion.querySelectorAll('[data-label-cell] span')
    // Only the older lessons' tables have measured label cells.
    if (spans.length === 0) {
      return
    }

    // find span with largest width
    const largestSpan = [...spans].reduce((a, b) => a.offsetWidth > b.offsetWidth ? a : b)
    const cellStyle = window.getComputedStyle(largestSpan.parentElement)
    const cellPadding = parseInt(cellStyle.paddingLeft) + parseInt(cellStyle.paddingRight)

    // set width of label header to that width
    tableQuestion.querySelector('[data-label-header]').style.width = `${largestSpan.offsetWidth + cellPadding + 10}px`
  }

  resetSlideScrolling(slide) {
    if (slide.querySelector('[data-scrollable-container]')) {
      slide.querySelector('[data-scrollable-container]').classList.remove("overflow-y-auto", "overflow-x-hidden")
    } else {
      slide.classList.remove('scrollable-slide')
    }
  }

  handleSlideScrolling(slide) {
    const blocksContainer = slide.querySelector('[data-blocks]')
    const height = parseInt(slide.parentElement.style.height)

    let contentHeight = 0

    if (blocksContainer) {
      contentHeight = Array.from(blocksContainer.querySelectorAll('[data-block]')).reduce((a, b) => a + b.offsetHeight, 0)
    } else if (slide.querySelector("turbo-frame")) {
      contentHeight = Array.from(slide.querySelector("turbo-frame").children).reduce((a, b) => a + b.offsetHeight, 0)
    } else {
      contentHeight = Array.from(slide.children).reduce((a, b) => a + b.offsetHeight, 0)
    }

    if (contentHeight > height && !slide.querySelector("[data-provider='youtube']")) {
      // slide.classList.remove("overflow-hidden")
      // slide.classList.add("overflow-y-auto", "overflow-x-hidden")
      if (slide.querySelector('[data-scrollable-container]')) {
        slide.querySelector('[data-scrollable-container]').classList.add("overflow-y-auto", "overflow-x-hidden")
      } else {
        slide.style.height = height + 'px'
        slide.classList.add('scrollable-slide')
      }
    }
  }

  innerSlideTargetConnected(innerSlide) {
    this.handleSlideScrolling(innerSlide.closest("section"))
  }

  resetVideos(slide) {
    const videoEls = slide.querySelectorAll('[data-controller="video-player"]')
    videoEls.forEach((el) => { el.parentElement.src = el.parentElement.src })
  }
}
